import {AppState} from "../../../../redux/rootReducer";
import {
  IMySongsLibraryFullLibrarySong,
  IMySongsLibraryUserLibraryLesson,
  mySongsLibraryStore
} from "./mySongsLibrary.store";
import {
  getIsMySongsResultsSearched,
  getIsMySongsResultsSearchTerm,
  getMySongsPageAvailableFilterSelectedArrangementTypes,
  getMySongsPageAvailableFilterSelectedGenres,
  getMySongsPageFilterLessonVideoOnly,
  getMySongsPageFilterResultsToDifficultyIds
} from "../my-songs-page-store/mySongsPage.selectors";
import {getSortLessonHandler, getSortSongsHandler} from "./mySongsLibrary.sort";
import {toLowercaseTrim} from "../../../../util/string-util";
import {DifficultyOption} from "../../../../models/view/common/DifficultyOption";
import {mySongsPageStore} from "../my-songs-page-store/mySongsPage.store";

/**
 * By and large, the selectors in this file use two stores to get the data they need:
 * - mySongsLibraryStore: This store contains the full library of songs and the user's library of songs.
 * - mySongsPageStore: This store contains the filters and search terms that the user has applied to the library.
 * Selectors which pull from two different stores should not be associated with any single store.
 * Alternately, the two stores could be merged.  However, the tradeoffs of doing so haven't been evaluated.
 */

const getFullLibraryFiltered = (store: AppState): IMySongsLibraryFullLibrarySong[] => {
  // genres to filter by
  const filterToGenres = getMySongsPageAvailableFilterSelectedGenres(store);
  // if we should filter by genres
  const shouldFilterByGenres = filterToGenres.length > 0;
  // if we should filter by only video lessons
  const shouldFilterByVideoOnly = getMySongsPageFilterLessonVideoOnly(store);
  // ArrangementTypes to filter by
  const filterToArrangementTypes = getMySongsPageAvailableFilterSelectedArrangementTypes(store);
  // if we should filter by ArrangementTypes
  const shouldFilterByArrangementTypes = filterToArrangementTypes.length > 0;
  // DifficultyIds to filter by
  const filterToDifficulties = getMySongsPageFilterResultsToDifficultyIds(store);
  // if we should filter by DifficultyIds
  const shouldFilterByDifficulties = filterToDifficulties.length > 0;

  // get full library
  return mySongsLibraryStore.getFullLibrary()
  // apply filters
    .filter(({genreId})=> !shouldFilterByGenres || filterToGenres.includes(genreId))
    .map((librarySong) => {
      const lessons = librarySong.lessons
        // filter by video only
        .filter(({hasVideo}) => !shouldFilterByVideoOnly || hasVideo)
        // filter by difficulty
        .filter(({difficultyId}) => !shouldFilterByDifficulties || filterToDifficulties.includes(difficultyId))
        // filter by arrangement type
        .filter(({arrangementTypeId}) => !shouldFilterByArrangementTypes || filterToArrangementTypes.includes(arrangementTypeId));

      return {
        ...librarySong,
        lessons,
      };
    })
    // remove any songs that the filter removed all lessons
    .filter(({lessons})=> lessons.length > 0);
}

const getFullLibraryResultsAfterFilter = (store: AppState): IMySongsLibraryFullLibrarySong[] => {
  const isResultsFiltered = mySongsPageStore.isFiltering();
  if (isResultsFiltered){
    return [ ...getFullLibraryFiltered(store),];
  }
  // If the results are not filtered, this will return lessons even if they have zero arrangements.
  // The method above will filter out songs with zero arrangements.
  return [ ...(mySongsLibraryStore.getFullLibrary()),];
}

const getFullLibraryUnsortedResults = (store: AppState): IMySongsLibraryFullLibrarySong[] => {
  const isResultsSearched = getIsMySongsResultsSearched(store);
  const results = getFullLibraryResultsAfterFilter(store);
  if (!isResultsSearched) {
    return results;
  }
  const searchTerm = getIsMySongsResultsSearchTerm(store);
  return results
    .filter(({name, artistName}) => {
      const searchName = toLowercaseTrim(name);
      const searchArtistName = toLowercaseTrim(artistName);
      const doesSearchTermMatchName = (
        searchTerm === searchName
        || searchName.includes(searchTerm)
      );
      const doesSearchTermMatchArtistName = (
        searchTerm === searchArtistName
        || searchArtistName.includes(searchTerm)
      );

      return (doesSearchTermMatchName || doesSearchTermMatchArtistName)
    });
}

const getFullLibraryFilteredAndSorted = (store: AppState): IMySongsLibraryFullLibrarySong[] => {
  const sortColumn = mySongsPageStore.getSortColumn();
  const sortDirection = mySongsPageStore.getSortDirection();
  const sortHandler = getSortSongsHandler(sortColumn, sortDirection);
  const filteredResults = getFullLibraryUnsortedResults(store);
  return filteredResults.sort(sortHandler);
}

export const getFullLibraryFilteredTotal = (store: AppState): number =>
  getFullLibraryUnsortedResults(store).length;

export interface IMySongsLibraryFullLibrarySongCardResult {
  songId: number;
  thumbnailUrl: string;
  name: string;
  artistName: string;
  hasVideo: boolean;
  lessonCount: number;
}

export const getFullLibrarySongCardResults = (store: AppState): IMySongsLibraryFullLibrarySongCardResult[] =>
  getFullLibraryFilteredAndSorted(store)
    .map<IMySongsLibraryFullLibrarySongCardResult>(
    ({
      songId,
      name,
      artistName,
      hasVideo,
      thumbnailUrl,
      lessons
    }) => ({songId, name, artistName, hasVideo, thumbnailUrl, lessonCount: lessons.length,})
  );

export const getDoesMySongsLibraryUserLibraryIncludeLessonId = (findLessonId: number) =>
  mySongsLibraryStore.getUserLibrary()
    .map(({lessonId}) => lessonId)
    .includes(findLessonId);

const getUserLibraryFiltered = (store: AppState): IMySongsLibraryUserLibraryLesson[] => {
  // genres to filter by
  const filterToGenres = getMySongsPageAvailableFilterSelectedGenres(store);
  // if we should filter by genres
  const shouldFilterByGenres = filterToGenres.length > 0;
  // if we should filter by only video lessons
  const shouldFilterByVideoOnly = getMySongsPageFilterLessonVideoOnly(store);
  // ArrangementTypes to filter by
  const filterToArrangementTypes = getMySongsPageAvailableFilterSelectedArrangementTypes(store);
  // if we should filter by ArrangementTypes
  const shouldFilterByArrangementTypes = filterToArrangementTypes.length > 0;
  // ArrangementTypes to filter by
  const filterToDifficulties = getMySongsPageFilterResultsToDifficultyIds(store);
  // if we should filter by ArrangementTypes
  const shouldFilterByDifficulties = filterToDifficulties.length > 0;

  // get user library
  return mySongsLibraryStore.getUserLibrary()
    // filter difficulty
    .filter(({difficultyId}) => !shouldFilterByDifficulties || filterToDifficulties.includes(difficultyId))
    // filter if lesson has video
    .filter(({hasVideo}) => !shouldFilterByVideoOnly || hasVideo)
    // filter genre
    .filter(({genreId}) => !shouldFilterByGenres || filterToGenres.includes(genreId))
    // filter arrangement type
    .filter(({arrangementTypeId}) => !shouldFilterByArrangementTypes || filterToArrangementTypes.includes(arrangementTypeId));
}

const getUserLibraryResultsAfterFilter = (store: AppState): IMySongsLibraryUserLibraryLesson[] => {
  const isResultsFiltered = mySongsPageStore.isFiltering();
  if (isResultsFiltered){
    return [ ...getUserLibraryFiltered(store),];
  }
  return [ ...(mySongsLibraryStore.getUserLibrary()),];
}

const getUserLibraryResultsAfterSearch = (store: AppState): IMySongsLibraryUserLibraryLesson[] => {
  const isResultsSearched = getIsMySongsResultsSearched(store);
  const results = getUserLibraryResultsAfterFilter(store);
  if (!isResultsSearched) {
    return results;
  }
  const searchTerm = getIsMySongsResultsSearchTerm(store);
  return results
    .filter(({name, artistName}) => {
      const searchName = toLowercaseTrim(name);
      const searchArtistName = toLowercaseTrim(artistName);
      const doesSearchTermMatchName = (
        searchTerm === searchName
        || searchName.includes(searchTerm)
      );
      const doesSearchTermMatchArtistName = (
        searchTerm === searchArtistName
        || searchArtistName.includes(searchTerm)
      );

      return (doesSearchTermMatchName || doesSearchTermMatchArtistName)
    });
}

const getUserLibraryFilteredAndSorted = (store: AppState): IMySongsLibraryUserLibraryLesson[] => {
  const sortColumn = mySongsPageStore.getSortColumn();
  const sortDirection = mySongsPageStore.getSortDirection();
  const sortHandler = getSortLessonHandler(sortColumn, sortDirection);
  return getUserLibraryResultsAfterSearch(store)
    .sort(sortHandler);
}

export interface ISongArrangementCardWithProgress {
  lessonId: number;
  title: string;
  subTitle: string;
  imageUrl: string;
  stars: number;
  isFavorite: boolean;
  icon: "video" | "none";
  difficultyIdentifier: DifficultyOption;
  difficultyName: string;
}

export const getUserLibrarySongCardResults = (store: AppState): ISongArrangementCardWithProgress[] =>
  getUserLibraryFilteredAndSorted(store)
    .map<ISongArrangementCardWithProgress>(
    ({lessonId, name, artistName, thumbnailUrl, quantityStars, isFavorited, hasVideo, difficultyIdentifier, difficultyName }) =>
      ({
        lessonId,
        title: name,
        subTitle: artistName,
        imageUrl: thumbnailUrl,
        stars: quantityStars,
        icon: hasVideo ? "video" : "none",
        isFavorite: isFavorited,
        difficultyIdentifier,
        difficultyName,
      }));

export const getUserLibraryFavoritedSongCardResults = (store: AppState): ISongArrangementCardWithProgress[] =>
  getUserLibrarySongCardResults(store)
    .filter(({isFavorite}) => !!isFavorite);
